import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import LoadingGrid from 'routes/HumanResourcesModule/components/LoadingGrid';
import { StyNoSpacingGridContainer } from 'routes/HumanResourcesModule/components/ui/StyGridContainer';
import { useGetDetailedEnvelopePayComponentHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/PayComponentHypothesis/helpers/hooks/payComponentHypothesis';
import { useGetDetailedAgentPayComponentHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/hooks/agentPayComponentHypothesis';
import PayComponentHypothesisResumeGrid from './PayComponentHypothesisResumeGrid';
import {
    getAgentPayComponentColDefs,
    getColDefs,
} from './helpers/colDefinitions';
import Accordion from 'components/SharedComponents/Accordion/Accordion';

type Props = {
    hrSimulation: HrSimulation;
};

const PayComponentHypothesesResume = ({
    hrSimulation,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { id: simulationId } = hrSimulation;

    const {
        data: detailedEnvelopePayComponentHypothesesResponse,
        isLoading: isDetailedEnvelopePayComponentHypothesesResponseLoading,
    } = useGetDetailedEnvelopePayComponentHypotheses(simulationId);

    const {
        data: detailedAgentPayComponentHypothesesResponse,
        isLoading: isDetailedAgentPayComponentHypothesesResponseLoading,
    } = useGetDetailedAgentPayComponentHypotheses(simulationId);

    const columnDefs = getColDefs({
        hrSimulation,
    });

    const agentColumnDefs = getAgentPayComponentColDefs({
        hrSimulation,
        agentPayComponentHypotheses:
            detailedAgentPayComponentHypothesesResponse ?? [],
    });

    return (
        <Accordion
            title={t(
                'humanResources.simulation.agentPayComponentHypothesesResume',
            )}
            defaultExpanded={false}
        >
            <Box sx={{ justifyContent: 'space-around', marginTop: '1rem' }}>
                {isDetailedAgentPayComponentHypothesesResponseLoading ? (
                    <LoadingGrid />
                ) : (
                    <>
                        <Typography
                            variant="body1Bold"
                            sx={(theme) => ({
                                display: 'block',
                                marginBottom: '1rem',
                                color: theme.palette.darkGrey,
                            })}
                        >
                            {t(
                                `humanResources.simulation.agentPayComponentResume`,
                            )}
                        </Typography>
                        <StyNoSpacingGridContainer>
                            <PayComponentHypothesisResumeGrid
                                detailedPayComponentHypothesis={
                                    detailedAgentPayComponentHypothesesResponse
                                }
                                columnDefs={agentColumnDefs}
                            />
                        </StyNoSpacingGridContainer>
                    </>
                )}

                {isDetailedEnvelopePayComponentHypothesesResponseLoading ? (
                    <LoadingGrid />
                ) : (
                    <>
                        <Typography
                            variant="body1Bold"
                            sx={(theme) => ({
                                display: 'block',
                                marginBottom: '1rem',
                                marginTop: '1rem',
                                color: theme.palette.darkGrey,
                            })}
                        >
                            {t(
                                `humanResources.simulation.envelopePayComponentResume`,
                            )}
                        </Typography>
                        <StyNoSpacingGridContainer>
                            <PayComponentHypothesisResumeGrid
                                detailedPayComponentHypothesis={
                                    detailedEnvelopePayComponentHypothesesResponse
                                }
                                columnDefs={columnDefs}
                                gridProps={{
                                    context: {
                                        envelopePayComponentHypotheses:
                                            detailedEnvelopePayComponentHypothesesResponse ??
                                            [],
                                    },
                                }}
                            />
                        </StyNoSpacingGridContainer>
                    </>
                )}
            </Box>
        </Accordion>
    );
};

export default memo(PayComponentHypothesesResume);
